// front/src/preview-mode/graph/index.ts
import { buildFunnels } from "./aggregates";
import { generateDevices } from "./devices";
import { DESTINATIONS, EVENT_DEFS } from "./entities";
import { createRng, PREVIEW_SEED } from "./seed";
import {
  PREVIOUS_OUTCOME_WEIGHTS,
  PREVIOUS_SESSION_COUNT,
  generateSessions,
} from "./sessions";
import { LOCATIONS, PROVIDERS, SERVICE_LINES, buildPageGraph } from "./site";
import { buildSurveys } from "./surveys";
import type { PreviewGraph } from "./types";

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export interface BuildArgs {
  seed?: number;
  windowEnd?: number;
}

export function buildPreviewGraph({
  seed = PREVIEW_SEED,
  windowEnd = Date.now(),
}: BuildArgs = {}): PreviewGraph {
  const rng = createRng(seed);
  const windowStart = windowEnd - THIRTY_DAYS_MS;
  const previousWindowEnd = windowStart - 1;
  const previousWindowStart = previousWindowEnd - THIRTY_DAYS_MS;
  const pageGraph = buildPageGraph();
  const devices = generateDevices(rng);
  const sessions = generateSessions(rng, {
    devices,
    pageGraph,
    windowStart,
    windowEnd,
  });
  const previousSessions = generateSessions(rng, {
    devices,
    pageGraph,
    windowStart: previousWindowStart,
    windowEnd: previousWindowEnd,
    outcomeWeights: PREVIOUS_OUTCOME_WEIGHTS,
    count: PREVIOUS_SESSION_COUNT,
  });
  const funnels = buildFunnels(sessions, windowEnd);
  const { surveys, surveyAggregates } = buildSurveys({
    rng,
    sessions,
    devices,
    windowEnd,
  });
  return {
    serviceLines: SERVICE_LINES,
    locations: LOCATIONS,
    providers: PROVIDERS,
    pageGraph,
    eventDefs: EVENT_DEFS,
    destinations: DESTINATIONS,
    devices,
    sessions,
    previousSessions,
    funnels,
    surveys,
    surveyAggregates,
    windowStart,
    windowEnd,
    previousWindowStart,
    previousWindowEnd,
  };
}

let cached: PreviewGraph | null = null;

export function getPreviewGraph(): PreviewGraph {
  if (!cached) cached = buildPreviewGraph();
  return cached;
}

export function __resetPreviewGraphCache(): void {
  cached = null;
}
